import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
  Bold, Italic, Underline as UIcon,
  Heading2, Heading3, List, ListOrdered, Quote, Undo, Redo, Pilcrow,
} from "lucide-react";

const btnCls =
  "p-2 rounded hover:bg-[color:var(--bf-surface)] transition-colors text-[color:var(--bf-text)]";

function ToolButton({ active, onClick, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={btnCls}
      style={{
        background: active ? "var(--bf-surface)" : "transparent",
        color: active ? "var(--bf-primary)" : "var(--bf-text)",
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange, placeholder, resetKey }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Placeholder.configure({ placeholder: placeholder || "Start typing your chapter…" }),
    ],
    content: value || "",
    onUpdate({ editor }) {
      onChange && onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose-book min-h-[55vh] focus:outline-none",
        "data-testid": "chapter-content-input",
        spellcheck: "true",
      },
    },
  });

  // Reset editor content when switching chapters
  useEffect(() => {
    if (editor && resetKey !== undefined) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!editor) return null;

  return (
    <div data-testid="rich-editor">
      <div
        className="flex flex-wrap items-center gap-1 mb-4 pb-3 border-b sticky top-16 z-10"
        style={{ borderColor: "var(--bf-border)", background: "var(--bf-bg-app)" }}
        data-testid="editor-toolbar"
      >
        <ToolButton title="Bold" testid="tb-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Italic" testid="tb-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Underline" testid="tb-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UIcon className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-5 mx-1" style={{ background: "var(--bf-border)" }} />
        <ToolButton title="Paragraph" testid="tb-p" active={editor.isActive("paragraph") && !editor.isActive("heading")} onClick={() => editor.chain().focus().setParagraph().run()}>
          <Pilcrow className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Heading 2" testid="tb-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Heading 3" testid="tb-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-5 mx-1" style={{ background: "var(--bf-border)" }} />
        <ToolButton title="Bulleted list" testid="tb-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Numbered list" testid="tb-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Quote" testid="tb-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-5 mx-1" style={{ background: "var(--bf-border)" }} />
        <ToolButton title="Undo" testid="tb-undo" onClick={() => editor.chain().focus().undo().run()}>
          <Undo className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Redo" testid="tb-redo" onClick={() => editor.chain().focus().redo().run()}>
          <Redo className="w-4 h-4" />
        </ToolButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
